import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerOverlay,
  Flex,
  Heading,
  Icon,
  IconButton,
  Text,
  VStack,
  useColorMode,
  useDisclosure,
} from '@chakra-ui/react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  MdAgriculture,
  MdApartment,
  MdAssignment,
  MdDarkMode,
  MdDashboard,
  MdHistory,
  MdLightMode,
  MdLocationOn,
  MdMenu,
  MdPalette,
  MdPeople,
  MdStorefront,
  MdManageAccounts,
  MdTune,
} from 'react-icons/md';
import { useAuth } from '../../contexts/AuthContext';
import { getPendingIndentCount } from '../../repositories/indent.repository';
import { useStaffAccess } from '../../utils/staffAccess';

const SIDEBAR_W = '232px';
const PENDING_INDENT_POLL_MS = 150000;

const mainNav = [
  { to: '/admin/dashboard', label: 'Dashboard', icon: MdDashboard },
  {
    to: '/admin/equipments/village-summary',
    label: 'Village summary',
    icon: MdLocationOn,
  },
];

const themeNav = [{ to: '/admin/theme', label: 'Theme', icon: MdPalette }];

/** Operational equipment workflows — above Master Data. */
const equipmentsNav = [
  {
    to: '/admin/equipments/fleet',
    label: 'Equipment fleet',
    icon: MdAgriculture,
  },
  {
    to: '/admin/equipments/indents',
    label: 'Indents / Requests',
    icon: MdAssignment,
  },
  { to: '/admin/equipments/villagers', label: 'Villagers', icon: MdPeople },
  { to: '/admin/equipments/vendors', label: 'Vendors', icon: MdStorefront },
];

const administrationNavAll = [
  {
    to: '/admin/administration/staff',
    label: 'Staff management',
    icon: MdManageAccounts,
    visibleKey: 'staff',
  },
  {
    to: '/admin/administration/activities',
    label: 'Activities',
    icon: MdHistory,
    visibleKey: 'activities',
  },
  {
    to: '/admin/administration/system-config',
    label: 'System configuration',
    icon: MdTune,
    visibleKey: 'system',
  },
];

const masterDataNav = [
  { to: '/admin/master-data/villages', label: 'Villages', icon: MdLocationOn },
  {
    to: '/admin/master-data/business-units',
    label: 'Business Unit & Site',
    icon: MdApartment,
  },
  {
    to: '/admin/master-data/equipments',
    label: 'Equipments Master',
    icon: MdAgriculture,
  },
];

function SidebarLink({ to, label, icon, badge, onNavigate }) {
  return (
    <NavLink
      to={to}
      style={{ textDecoration: 'none' }}
      onClick={() => onNavigate?.()}
    >
      {({ isActive }) => (
        <Flex
          align="center"
          gap={2.5}
          px={3}
          py={2.5}
          borderRadius="sm"
          bg={isActive ? 'utkarsh.amber' : 'transparent'}
          color={isActive ? 'utkarsh.ink' : 'whiteAlpha.900'}
          fontWeight={isActive ? '600' : '500'}
          fontSize="15px"
          lineHeight="1.25"
          _hover={{ bg: isActive ? 'utkarsh.amber' : 'whiteAlpha.100' }}
        >
          <Icon as={icon} boxSize={5} flexShrink={0} />
          <Text as="span" flex={1}>
            {label}
          </Text>
          {badge > 0 ? (
            <Flex
              minW="20px"
              h="20px"
              px={1}
              align="center"
              justify="center"
              borderRadius="full"
              bg={isActive ? 'utkarsh.ink' : 'utkarsh.amber'}
              color={isActive ? 'white' : 'utkarsh.ink'}
              fontSize="11px"
              fontWeight="700"
              lineHeight="1"
              flexShrink={0}
            >
              {badge > 99 ? '99+' : badge}
            </Flex>
          ) : null}
        </Flex>
      )}
    </NavLink>
  );
}

function NavSection({ title, items, onNavigate }) {
  return (
    <Box>
      {title ? (
        <Text
          fontSize="11px"
          fontWeight="700"
          letterSpacing="0.06em"
          textTransform="uppercase"
          color="whiteAlpha.500"
          px={3}
          mb={2}
          mt={5}
        >
          {title}
        </Text>
      ) : null}
      <VStack align="stretch" spacing={1}>
        {items.map((item) => (
          <SidebarLink key={item.to} {...item} onNavigate={onNavigate} />
        ))}
      </VStack>
    </Box>
  );
}

function SidebarNav({
  mainNavItems,
  equipmentsNavItems,
  administrationNav,
  masterDataNavItems,
  themeNavItems,
  onNavigate,
}) {
  return (
    <>
      <Heading size="md" mb={1} color="utkarsh.amber" letterSpacing="-0.02em" flexShrink={0}>
        Utkarsh
      </Heading>
      <Text fontSize="13px" color="whiteAlpha.700" mb={4} flexShrink={0}>
        Admin panel
      </Text>

      <Box flex="1" minH={0} overflowY="auto" pr={1}>
        <NavSection items={mainNavItems} onNavigate={onNavigate} />
        <NavSection title="Equipments" items={equipmentsNavItems} onNavigate={onNavigate} />
        {administrationNav.length ? (
          <NavSection
            title="Administration"
            items={administrationNav}
            onNavigate={onNavigate}
          />
        ) : null}
        <NavSection title="Master Data" items={masterDataNavItems} onNavigate={onNavigate} />
        <Box mt={5}>
          <NavSection items={themeNavItems} onNavigate={onNavigate} />
        </Box>
      </Box>
    </>
  );
}

export default function AdminLayout() {
  const { logout, staff } = useAuth();
  const {
    canAccessStaffManagement,
    canViewStaffActivities,
    canViewSystemConfig,
  } = useStaffAccess();
  const navigate = useNavigate();
  const { colorMode, toggleColorMode } = useColorMode();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [pendingIndents, setPendingIndents] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const loadPending = async () => {
      try {
        const { pending } = await getPendingIndentCount();
        if (!cancelled) setPendingIndents(pending);
      } catch {
        // Keep the last known count; polling should stay quiet.
      }
    };
    loadPending();
    const timer = setInterval(loadPending, PENDING_INDENT_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, []);

  const displayName = staff?.fullName || staff?.name || staff?.email || 'Staff';
  const displayCode = staff?.staffId || staff?.code || '';

  const administrationNav = useMemo(() => {
    const visibility = {
      staff: canAccessStaffManagement,
      activities: canViewStaffActivities,
      system: canViewSystemConfig,
    };
    return administrationNavAll.filter((item) => visibility[item.visibleKey]?.());
  }, [canAccessStaffManagement, canViewStaffActivities, canViewSystemConfig]);

  const equipmentsNavItems = useMemo(
    () =>
      equipmentsNav.map((item) =>
        item.to === '/admin/equipments/indents'
          ? { ...item, badge: pendingIndents }
          : item,
      ),
    [pendingIndents],
  );

  const handleLogout = async () => {
    await logout();
    navigate('/auth/sign-in');
  };

  const navProps = {
    mainNavItems: mainNav,
    equipmentsNavItems,
    administrationNav,
    masterDataNavItems: masterDataNav,
    themeNavItems: themeNav,
  };

  return (
    <Flex h="100vh" overflow="hidden" bg="bg.canvas">
      <Box
        as="aside"
        w={SIDEBAR_W}
        minW={SIDEBAR_W}
        h="100vh"
        position="sticky"
        top={0}
        bg="bg.sidebar"
        color="white"
        px={3}
        py={5}
        display={{ base: 'none', lg: 'flex' }}
        flexDirection="column"
        flexShrink={0}
        overflow="hidden"
      >
        <SidebarNav {...navProps} />
      </Box>

      <Drawer isOpen={isOpen} placement="left" onClose={onClose}>
        <DrawerOverlay />
        <DrawerContent bg="bg.sidebar" color="white" maxW={SIDEBAR_W}>
          <DrawerCloseButton color="whiteAlpha.800" />
          <DrawerBody px={3} py={5} display="flex" flexDirection="column">
            <SidebarNav {...navProps} onNavigate={onClose} />
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      <Flex direction="column" flex="1" minW={0} h="100vh" overflow="hidden">
        <Flex
          as="header"
          align="center"
          justify="space-between"
          px={{ base: 3, md: 6 }}
          py={3}
          borderBottom="1px solid"
          borderColor="border.subtle"
          bg="bg.surface"
          flexShrink={0}
          gap={3}
        >
          <Flex align="center" gap={2} minW={0}>
            <IconButton
              aria-label="Open menu"
              icon={<MdMenu />}
              onClick={onOpen}
              variant="ghostInk"
              size="sm"
              display={{ base: 'inline-flex', lg: 'none' }}
            />
            <Text fontWeight="600" color="text.primary" fontSize="14px" noOfLines={1}>
              Admin
            </Text>
          </Flex>
          <Flex align="center" gap={{ base: 2, md: 3 }} flexShrink={0}>
            <Box textAlign="right" lineHeight="1.2" display={{ base: 'none', sm: 'block' }}>
              <Text fontSize="13px" fontWeight="600" color="text.primary" noOfLines={1}>
                {displayName}
              </Text>
              {displayCode ? (
                <Text fontSize="11px" color="text.secondary">
                  {displayCode}
                </Text>
              ) : null}
            </Box>
            <IconButton
              aria-label="Toggle color mode"
              icon={colorMode === 'light' ? <MdDarkMode /> : <MdLightMode />}
              onClick={toggleColorMode}
              variant="ghostInk"
              size="sm"
            />
            <Button size="sm" variant="outlineInk" onClick={handleLogout}>
              Logout
            </Button>
          </Flex>
        </Flex>
        <Box
          as="main"
          flex="1"
          minH={0}
          overflowY="auto"
          bg="bg.canvas"
          px={{ base: 3, md: 6 }}
          py={{ base: 4, md: 6 }}
          w="100%"
        >
          <Outlet />
        </Box>
      </Flex>
    </Flex>
  );
}
